import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip } from '../ui/Tooltip';
import { 
  Edit2,
  Trash2,
  Eye,
  Globe,
  Mail,
  Phone,
  User,
  FileText,
  Flag,
  ChevronRight,
  Plus,
  Clock,
  TrendingUp
} from 'react-feather';
import { IconButton } from '../ui/IconButton';
import OneClickContact from './OneClickContact';
import ContactFlowChart from './ContactFlowChart';
// Removed deleted hook import - using statusManager prop instead
import api from '../../services/api';
import './PartnerCompanyCard.css';

const PartnerCompanyCard = ({ 
  company, 
  onEdit, 
  onView,
  onDelete, 
  onStatusUpdate,
  getStatusColor, // Deprecated: kept for backward compatibility
  getStatusIcon, // Deprecated: kept for backward compatibility
  formatStatus, // Deprecated: kept for backward compatibility
  viewMode,
  onContactLogged,
  statusManager // New: database-driven status management (optional, will create own if not provided)
}) => {
  // Use provided statusManager or create our own
  const activeStatusManager = statusManager;
  
  const [expanded, setExpanded] = useState(false);
  const [showQuickLog, setShowQuickLog] = useState(false);
  const [contactAttempts, setContactAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Helper functions
  const parseJson = (data) => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    }
    return data;
  };

  const getTechnologyStack = () => {
    const techStack = parseJson(company.technology_stack);
    return Array.isArray(techStack) ? techStack : [];
  };

  const getSkillCategories = () => {
    const skillCategories = parseJson(company.skill_categories);
    return Array.isArray(skillCategories) ? skillCategories : [];
  };

  // Fetch contact attempts count when component mounts or company changes
  useEffect(() => {
    const fetchContactAttempts = async () => {
      try {
        const response = await api.get(`/api/contact-attempts/partner/${company.id}/history`);
        setContactAttempts(response.data.history || []);
      } catch (error) {
        console.error('Error fetching contact attempts:', error);
      }
    };

    fetchContactAttempts();
  }, [company.id, company.recent_actions]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete(company.id);
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Database-driven status options (replaces hardcoded switch statement)
  const getAvailableStatusOptions = (currentStatus) => {
    if (!activeStatusManager.isReady) {
      // Fallback while loading
      return [{ value: currentStatus, label: currentStatus }];
    }
    
    // Use the status manager's method for consistent formatting
    return activeStatusManager.getAvailableStatusOptions(currentStatus);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    
    // If changing to "contacted" status, show quick log
    if (newStatus === 'contacted') {
      handleShowQuickLog();
    } else if (newStatus === 'onboarding_complete') {
      // Auto-save "Onboarding Complete" as "Fully Onboarded"
      console.log('🎯 Onboarding Complete selected → Auto-saving as "Fully Onboarded"');
      onStatusUpdate(company.id, 'fully_onboarded');
    } else {
      onStatusUpdate(company.id, newStatus);
    }
  };

  const handleQuickLogSuccess = (contactAttempt) => {
    setShowQuickLog(false);
    document.body.classList.remove('overlay-active');
    
    // Set loading state to show refresh indicator
    setLoading(true);
    
    // Notify parent component to refresh company data
    if (onContactLogged) {
      onContactLogged(company.id, contactAttempt);
    }
    
    // Clear loading state after a short delay to allow parent to update
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleOverlayClick = (e) => {
    // Close overlay when clicking on the background
    if (e.target === e.currentTarget) {
      setShowQuickLog(false);
      document.body.classList.remove('overlay-active');
    }
  };

  const handleShowQuickLog = () => {
    setShowQuickLog(true);
    document.body.classList.add('overlay-active');
  };

  // Cleanup effect to remove overlay-active class when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('overlay-active');
    };
  }, []);

  const getContactPersonName = () => {
    // Helper function to safely parse JSON
    const parseJson = (data) => {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (e) {
          return null;
        }
      }
      return data;
    };

    // Helper function to convert to Title case
    const toTitleCase = (str) => {
      if (!str) return str;
      return str.replace(/\w\S*/g, (txt) => 
        txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );
    };

    // Check for primary_contact first (new structure)
    const primaryContact = parseJson(company.primary_contact);
    if (primaryContact && primaryContact.name) {
      return toTitleCase(primaryContact.name);
    }
    
    // Check for contact_persons array (legacy structure)
    const contactPersons = parseJson(company.contact_persons);
    if (contactPersons && contactPersons.length > 0) {
      return toTitleCase(contactPersons[0].name || 'Contact Person');
    }
    
    // Check if we have a decision maker
    const decisionMaker = parseJson(company.decision_maker);
    if (decisionMaker && decisionMaker.name) {
      return toTitleCase(decisionMaker.name);
    }
    
    return 'No contact person';
  };

  const getContactPersonPhone = () => {
    // Helper function to safely parse JSON
    const parseJson = (data) => {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (e) {
          return null;
        }
      }
      return data;
    };

    // Check for primary_contact first (new structure)
    const primaryContact = parseJson(company.primary_contact);
    if (primaryContact && primaryContact.phone) {
      return primaryContact.phone;
    }
    
    // Check for contact_persons array (legacy structure)
    const contactPersons = parseJson(company.contact_persons);
    if (contactPersons && contactPersons.length > 0) {
      return contactPersons[0].phone || null;
    }
    
    // Check if we have a decision maker
    const decisionMaker = parseJson(company.decision_maker);
    if (decisionMaker && decisionMaker.phone) {
      return decisionMaker.phone;
    }
    
    return null;
  };

  const shouldShowWillingToWorkBadge = () => {
    return company.willing_to_work === true || company.willing_to_work === 'true';
  };

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Get attempt counts
  const getAttemptCounts = () => {
    if (!contactAttempts) return { total: 0, phone: 0, email: 0 };
    
    const phoneAttempts = contactAttempts.filter(attempt => attempt.contact_method === 'phone').length;
    const emailAttempts = contactAttempts.filter(attempt => attempt.contact_method === 'email').length;
    
    return {
      total: contactAttempts.length,
      phone: phoneAttempts,
      email: emailAttempts
    };
  };

  const attemptCounts = getAttemptCounts();

  // Helper function to format recent action date
  const formatActionDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Helper function to format follow-up date
  const formatFollowUpDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays < 0) {
      return `${Math.abs(diffDays)}d overdue`;
    } else if (diffDays <= 3) {
      return `In ${diffDays}d`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    }
  };

  return (
    <div className={`company-card ${expanded ? 'expanded' : ''} ${viewMode === 'list' ? 'list-view' : ''}`}>
      {/* Header */}
      <div className="company-header">
        <div className="company-title">
          <h3>{company.name}</h3>
          {company.location && (
            <span className="company-location">{company.location}</span>
          )}
        </div>
        <div className="company-actions">
          {/* Toggle Expand Icon */}
          <IconButton
            variant="neutral-tertiary"
            size="sm"
                          icon={expanded ? <ChevronRight style={{ transform: 'rotate(90deg)' }} /> : <ChevronRight />}
            onClick={toggleExpanded}
            title={expanded ? "Collapse" : "Expand"}
          />
          <IconButton
            variant="neutral-tertiary"
            size="sm"
                          icon={<Eye />}
            onClick={() => onView(company)}
            title="View Details"
          />
          <IconButton
            variant="neutral-tertiary"
            size="sm"
                          icon={<Edit2 />}
            onClick={() => onEdit(company)}
            title="Edit Company"
          />
          <IconButton
            variant="neutral-tertiary"
            size="sm"
                          icon={<Trash2 />}
            onClick={handleDelete}
            title="Delete Company"
          />
        </div>
      </div>

      {/* Essential Info - Always Visible */}
      <div className="company-essential">
        {/* Status */}
        <div className="company-status">
          {getStatusIcon(company.partner_status?.current_status || 'initial_data_entered')}
          <Badge variant={getStatusColor(company.partner_status?.current_status || 'initial_data_entered')}>
            {formatStatus(company.partner_status?.current_status || 'initial_data_entered')}
          </Badge>
          <select
            value={company.partner_status?.current_status || 'initial_data_entered'}
            onChange={handleStatusChange}
            className="status-select"
            disabled={
              company.partner_status?.current_status === 'contacted' ||
              company.partner_status?.current_status === 'fully_onboarded' ||
              company.partner_status?.current_status === 'agreement_signed' || 
              company.partner_status?.current_status === 'rejected_not_interested' ||
              company.partner_status?.current_status === 'not_eligible' ||
              company.partner_status?.current_status === 'not_interested'
            }
            title={
              company.partner_status?.current_status === 'contacted'
                ? 'Use Quick Log to update status based on contact outcome'
                : company.partner_status?.current_status === 'fully_onboarded' || company.partner_status?.current_status === 'agreement_signed' || company.partner_status?.current_status === 'rejected_not_interested' || company.partner_status?.current_status === 'not_eligible' || company.partner_status?.current_status === 'not_interested'
                ? 'Final status - cannot be changed'
                : 'Change company status'
            }
          >
            {getAvailableStatusOptions(company.partner_status?.current_status || 'initial_data_entered').map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Quick Log Button - Only show when status is "contacted" */}
          {company.partner_status?.current_status === 'contacted' && (
            <Button
              variant="brand-primary"
              size="sm"
              icon={<Plus />}
              onClick={(e) => {
                e.stopPropagation();
                handleShowQuickLog();
              }}
              className="quick-log-button highlighted"
              title="Log contact outcome to progress status"
            >
              ✨ Quick Log
            </Button>
          )}
        </div>

        {/* Contact Attempts Summary */}
        {attemptCounts.total > 0 && (
          <div className="contact-attempts-summary">
            <div className="attempt-counts">
              <Badge variant="neutral-secondary" size="sm">
                📞 {attemptCounts.phone} calls
              </Badge>
              <Badge variant="neutral-secondary" size="sm">
                📧 {attemptCounts.email} emails
              </Badge>
              <Badge variant="neutral-secondary" size="sm">
                Total: {attemptCounts.total}
              </Badge>
            </div>
            {attemptCounts.total >= 5 && (
              <Badge variant="warning" size="sm">
                ⚠️ Consider bypass options
              </Badge>
            )}
          </div>
        )}

        {/* Recent Actions Summary - Only show for contacted status */}
        {company.partner_status?.current_status === 'contacted' && 
         company.recent_actions && 
         company.recent_actions.length > 0 && (
          <div className="recent-actions-badges">
            <div className="actions-header">
              <span className="actions-label">Latest Statuses</span>
              <span className="actions-count">({company.recent_actions.length})</span>
              {loading && <span className="refresh-indicator">🔄</span>}
            </div>
            <div className="actions-badges">
              {/* Show the latest 2 statuses from the most recent contact attempts */}
              {company.recent_actions.slice(0, 2).map((action, index) => (
                <div key={action.id} className={`action-badge ${['wrong_person', 'follow_up_required', 'interested', 'not_interested', 'no_response', 'company_closed', 'number_disconnected'].includes(action.outcome) ? action.outcome : action.status}`}>
                  <div className="badge-main">
                    <span className="badge-icon">
                      {action.outcome === 'wrong_person' ? '❌' :
                       action.outcome === 'follow_up_required' ? '🔄' :
                       action.outcome === 'interested' ? '👍' :
                       action.outcome === 'not_interested' ? '👎' :
                       action.outcome === 'no_response' ? '⏳' :
                       action.outcome === 'company_closed' ? '🏢' :
                       action.outcome === 'number_disconnected' ? '📞' :
                       action.method === 'phone' ? '📞' : 
                       action.method === 'email' ? '📧' : '💼'}
                    </span>
                    <span className="badge-text">
                      {action.outcome === 'wrong_person' ? 'Wrong Number' :
                       action.outcome === 'follow_up_required' ? 'Call Back' :
                       action.outcome === 'interested' ? 'Interested' :
                       action.outcome === 'not_interested' ? 'Not Interested' :
                       action.outcome === 'no_response' ? 'No Response' :
                       action.outcome === 'company_closed' ? 'Company Closed' :
                       action.outcome === 'number_disconnected' ? 'Number Disconnected' :
                       action.status === 'phone_called' ? 'Called' :
                       action.status === 'phone_answered' ? 'Answered' :
                       action.status === 'phone_no_answer' ? 'No Answer' :
                       action.status === 'phone_voicemail' ? 'Voicemail' :
                       action.status === 'phone_busy' ? 'Busy' :
                       action.status === 'email_sent' ? 'Sent' :
                       action.status === 'email_bounced' ? 'Bounced' :
                       action.status === 'email_opened' ? 'Opened' :
                       action.status === 'email_clicked' ? 'Clicked' :
                       action.status === 'completed' ? 'Completed' :
                       action.status === 'cancelled' ? 'Cancelled' :
                       action.status === 'draft' ? 'Draft' :
                       action.status === 'sent' ? 'Sent' :
                       action.status === 'delivered' ? 'Delivered' :
                       action.status === 'failed' ? 'Failed' :
                       action.status}
                    </span>
                  </div>
                  <div className="badge-meta">
                    <span className="badge-user">{action.user}</span>
                    <span className="badge-date">{formatActionDate(action.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Latest Follow-up - Only show for contacted status with scheduled follow-ups */}
        {company.partner_status?.current_status === 'contacted' && 
         company.latest_follow_up && (
          <div className="follow-up-badge">
            <div className="follow-up-header">
              <span className="follow-up-icon">📅</span>
              <span className="follow-up-label">Follow-up</span>
            </div>
            <div className="follow-up-content">
              <div className="follow-up-main">
                <span className="follow-up-date">
                  {formatFollowUpDate(company.latest_follow_up.scheduled_date)}
                </span>
                <span className="follow-up-method">
                  {company.latest_follow_up.method === 'phone' ? '📞' : 
                   company.latest_follow_up.method === 'email' ? '📧' : '💼'}
                </span>
              </div>
              {company.latest_follow_up.notes && (
                <div className="follow-up-notes">
                  {company.latest_follow_up.notes}
                </div>
              )}
              <div className="follow-up-meta">
                <span className="follow-up-user">{company.latest_follow_up.user}</span>
              </div>
            </div>
          </div>
        )}

        {/* Willing to Work Badge */}
        {shouldShowWillingToWorkBadge() && (
          <div className="willing-to-work-badge">
            <Badge variant="success" size="sm">
              🟢 WILLING TO WORK
            </Badge>
          </div>
        )}

        {/* Primary Contact - Always Visible */}
        <div className="primary-contact">
          <User size={14} />
          <span className="contact-name">{getContactPersonName()}</span>
          {getContactPersonPhone() && (
            <a 
              href={`tel:${getContactPersonPhone()}`}
              className="contact-phone"
            >
              {getContactPersonPhone()}
            </a>
          )}
        </div>
      </div>

      {/* Expanded Content - Only Visible When Expanded */}
      {viewMode === 'grid' && expanded && (
        <div className="company-expanded">
          {/* Contact Details */}
          <div className="expanded-section">
            <h4 className="section-title">Contact Information</h4>
            <div className="expanded-details">
              {company.website && (
                <div className="detail-item">
                  <Globe size={14} />
                  <span className="detail-label">Website:</span>
                  <a 
                    href={company.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="detail-value link"
                  >
                    {company.website}
                  </a>
                </div>
              )}
              
              {company.email && (
                <div className="detail-item">
                  <Mail size={14} />
                  <span className="detail-label">Email:</span>
                  <a 
                    href={`mailto:${company.email}`}
                    className="detail-value link"
                  >
                    {company.email}
                  </a>
                </div>
              )}
              
              {company.phone && (
                <div className="detail-item">
                  <Phone size={14} />
                  <span className="detail-label">Org. Phone:</span>
                  <a 
                    href={`tel:${company.phone}`}
                    className="detail-value link"
                  >
                    {company.phone}
                  </a>
                </div>
              )}
              
              {company.assigned_to_user && (
                <div className="detail-item">
                  <User size={14} />
                  <span className="detail-label">Assigned:</span>
                  <span className="detail-value">
                    {company.assigned_to_user.display_name || company.assigned_to_user.email}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Technology Stack and Skills (if more than 3) */}
          {(getTechnologyStack().length > 3 || getSkillCategories().length > 3) && (
            <div className="expanded-section">
              <h4 className="section-title">Additional Capabilities</h4>
              <div className="tech-skills-expanded">
                {getTechnologyStack().length > 3 && (
                  <div className="tech-skills-group">
                    <div className="tech-skills-label">More Tech Stack:</div>
                    <div className="tech-skills-badges">
                      {getTechnologyStack().slice(3).map((tech, index) => (
                        <Tooltip
                          key={index + 3}
                          title="Technology"
                          description={tech}
                          placement="top"
                          delay={[200, 0]}
                        >
                          <Badge 
                            variant="neutral-tertiary"
                            size="sm"
                            className="tech-skill-badge"
                          >
                            {tech}
                          </Badge>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}

                {getSkillCategories().length > 3 && (
                  <div className="tech-skills-group">
                    <div className="tech-skills-label">More Skills:</div>
                    <div className="tech-skills-badges">
                      {getSkillCategories().slice(3).map((skill, index) => (
                        <Tooltip
                          key={index + 3}
                          title="Skill"
                          description={skill}
                          placement="top"
                          delay={[200, 0]}
                        >
                          <Badge 
                            variant="neutral-tertiary"
                            size="sm"
                            className="tech-skill-badge"
                          >
                            {skill}
                          </Badge>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details - For List View */}
      {viewMode === 'list' && (
        <div className="company-details">
          {company.website && (
            <div className="detail-item">
              <Globe size={14} />
              <span className="detail-label">Website:</span>
              <a 
                href={company.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="detail-value link"
              >
                {company.website}
              </a>
            </div>
          )}
          
          {company.email && (
            <div className="detail-item">
              <Mail size={14} />
              <span className="detail-label">Email:</span>
              <a 
                href={`mailto:${company.email}`}
                className="detail-value link"
              >
                {company.email}
              </a>
            </div>
          )}
          
          {company.phone && (
            <div className="detail-item">
              <Phone size={14} />
              <span className="detail-label">Org. Phone:</span>
              <a 
                href={`tel:${company.phone}`}
                className="detail-value link"
              >
                {company.phone}
              </a>
            </div>
          )}
          
          {company.assigned_to_user && (
            <div className="detail-item">
              <User size={14} />
              <span className="detail-label">Assigned:</span>
              <span className="detail-value">
                {company.assigned_to_user.display_name || company.assigned_to_user.email}
              </span>
            </div>
          )}

          {/* Technology Stack and Skills - List View */}
          {getTechnologyStack().length < 0 && (
            <div className="detail-item">
              <FileText size={14} />
              <span className="detail-label">Tech Stacks:</span>
              <span className="detail-value">
                {getTechnologyStack().slice(0, 5).join(', ')}
                {getTechnologyStack().length > 5 && ` +${getTechnologyStack().length - 5} more`}
              </span>
            </div>
          )}

          {getSkillCategories().length < 0 && (
            <div className="detail-item">
              <Flag size={14} />
              <span className="detail-label">Skills:</span>
              <span className="detail-value">
                {getSkillCategories().slice(0, 5).join(', ')}
                {getSkillCategories().length > 5 && ` +${getSkillCategories().length - 5} more`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Technology Stack and Skills - Always Visible */}
      <div className="company-tech-skills">
        {/* Technology Stack */}
        {getTechnologyStack().length > 0 && (
          <div className="tech-skills-section">
            <div className="tech-skills-label">Tech Stack:</div>
            <div className="tech-skills-badges">
              {getTechnologyStack().slice(0, 3).map((tech, index) => (
                <Tooltip
                  key={index}
                  title="Technology"
                  description={tech}
                  placement="top"
                  delay={[200, 0]}
                >
                  <Badge 
                    variant="neutral-tertiary"
                    size="sm"
                    className="tech-skill-badge"
                  >
                    {tech}
                  </Badge>
                </Tooltip>
              ))}
              {getTechnologyStack().length > 3 && (
                                  <Tooltip
                    title="Additional Tech Stack"
                    description={getTechnologyStack().slice(3).join(', ')}
                    placement="top"
                    delay={[300, 0]}
                  >
                    <Badge 
                      variant="neutral-tertiary"
                      size="sm"
                      className="tech-skill-badge more-badge"
                    >
                      +{getTechnologyStack().length - 3}
                    </Badge>
                  </Tooltip>
              )}

            </div>
          </div>
        )}

        {/* Skill Categories */}
        {getSkillCategories().length > 0 && (
          <div className="tech-skills-section">
            <div className="tech-skills-label">Skills:</div>
            <div className="tech-skills-badges">
              {getSkillCategories().slice(0, 3).map((skill, index) => (
                <Tooltip
                  key={index}
                  title="Skill"
                  description={skill}
                  placement="top"
                  delay={[200, 0]}
                >
                  <Badge 
                    variant="neutral-tertiary"
                    size="sm"
                    className="tech-skill-badge"
                  >
                    {skill}
                  </Badge>
                </Tooltip>
              ))}
              {getSkillCategories().length > 3 && (
                                  <Tooltip
                    title="Additional Skills"
                    description={getSkillCategories().slice(3).join(', ')}
                    placement="top"
                    delay={[300, 0]}
                  >
                    <Badge 
                      variant="neutral-tertiary"
                      size="sm"
                      className="tech-skill-badge more-badge"
                    >
                      +{getSkillCategories().length - 3}
                    </Badge>
                  </Tooltip>
              )}

            </div>
          </div>
        )}
      </div>



      {/* Footer */}
      <div className="company-footer">
        <div className="footer-left">
          <span>Created {formatDate(company.created_at)}</span>
          {company.created_by_user && (
            <span>by {company.created_by_user.display_name}</span>
          )}
        </div>
        <div className="footer-right">
          {company.communication_logs && company.communication_logs > 0 && (
            <span>{company.communication_logs} communications</span>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h4>Delete Company</h4>
            <p>Are you sure you want to delete "{company.name}"? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <Button
                variant="neutral-tertiary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

                   {/* Quick Log Overlay - For existing contacted companies */}
             {showQuickLog && (
               <div className="quick-log-overlay" onClick={handleOverlayClick}>
                 <div className="quick-log-content">
                   <OneClickContact
                     partner={company}
                     onSuccess={(contactAttempt) => {
                       handleQuickLogSuccess(contactAttempt);
                     }}
                     onCancel={() => {
                       setShowQuickLog(false);
                       document.body.classList.remove('overlay-active');
                       // Reset status select to previous value if it was changed
                       const select = document.querySelector(`[data-company-id="${company.id}"] .status-select`);
                       if (select && select.value === 'contacted' && company.partner_status?.current_status !== 'contacted') {
                         select.value = company.partner_status?.current_status || 'initial_data_entered';
                       }
                     }}
                     onStatusUpdate={onStatusUpdate}
                   />
                 </div>
               </div>
             )}

             {/* Contact Flow Chart Overlay */}
             {/* This section is removed as the flow chart is now integrated into OneClickContact */}
    </div>
  );
};

export default PartnerCompanyCard; 